import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { BarChart3, Clock, SkipForward, Ban, Zap, Layers } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#6366f1'];

const StatCard = ({ icon: Icon, label, value, sub, color = '#1d4ed8' }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white">
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}10` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div>
      <p className="text-lg font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      <p className="text-xs text-[#475569]">{label}</p>
      {sub && <p className="text-[10px] text-[#94A3B8]">{sub}</p>}
    </div>
  </div>
);

export const EfficiencyMetrics = ({ qrId }) => {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, [qrId]);

  const fetchMetrics = async () => {
    try {
      const res = await axios.get(`${API}/intelligence/efficiency-metrics/${qrId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMetrics(res.data);
    } catch {
      toast.error('Failed to load efficiency metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!metrics || metrics.total_sessions === 0) {
    return (
      <Card className="p-6 border border-[#E2E8F0]" data-testid="efficiency-metrics-panel">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Interview Efficiency</h2>
        </div>
        <p className="text-sm text-[#475569] text-center py-6">
          No interview data yet. Efficiency metrics will appear after sessions are conducted.
        </p>
      </Card>
    );
  }

  const formatDuration = (secs) => {
    if (!secs) return 'N/A';
    if (secs < 60) return `${Math.round(secs)}s`;
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `${m}m ${s}s`;
  };

  // Termination reasons pie chart
  const termData = metrics.termination_reasons
    ? Object.entries(metrics.termination_reasons)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({
          name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: v,
        }))
    : [];

  // Playbook breakdown bar chart
  const pbData = metrics.completion_vs_abandonment_by_playbook
    ? Object.entries(metrics.completion_vs_abandonment_by_playbook).map(([name, stats]) => ({
        name: name.length > 18 ? name.slice(0, 16) + '...' : name,
        Completed: stats.completed || 0,
        Abandoned: stats.abandoned || 0,
      }))
    : [];

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="efficiency-metrics-panel">
      <div className="flex items-center gap-2 mb-5">
        <BarChart3 className="h-5 w-5 text-[#1d4ed8]" />
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Interview Efficiency
        </h2>
        <span className="text-xs bg-[#EFF6FF] text-[#1d4ed8] px-2 py-0.5 rounded-full font-medium">
          Conversational Intelligence
        </span>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="efficiency-stat-cards">
        <StatCard
          icon={Clock}
          label="Avg Duration"
          value={formatDuration(metrics.average_interview_duration)}
          color="#1d4ed8"
        />
        <StatCard
          icon={SkipForward}
          label="AI Skip Rate"
          value={`${metrics.questions_skipped_by_ai_rate || 0}%`}
          sub="Questions skipped by AI"
          color="#8b5cf6"
        />
        <StatCard
          icon={Ban}
          label="Early Termination"
          value={`${metrics.early_termination_rate || 0}%`}
          sub="Low signal exits"
          color="#ef4444"
        />
        <StatCard
          icon={Layers}
          label="Avg Fields Extracted"
          value={metrics.average_fields_extracted || 0}
          sub="Per interview"
          color="#22c55e"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Termination reasons pie */}
        {termData.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Termination Reasons
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={termData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {termData.map((_, i) => (
                    <Cell key={`item-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Playbook breakdown bar */}
        {pbData.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Completion by Playbook
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={pbData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#475569" tick={{ fontSize: 11 }} />
                <YAxis stroke="#475569" />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="Completed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Abandoned" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Summary footer */}
      <div className="mt-4 pt-3 border-t border-[#E2E8F0] flex items-center gap-4 text-xs text-[#475569]">
        <span><Zap className="h-3 w-3 inline mr-1" />{metrics.total_sessions} total sessions</span>
        <span>{metrics.total_completed} completed</span>
        <span>{metrics.total_abandoned} abandoned</span>
      </div>
    </Card>
  );
};
